import {JSX, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Link,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {useAuth} from "../../auth/AuthContext";
import {getTools, openPortal, startCheckout, Tool} from "../../utils/billingUtil";
import {
    getReleaseNotificationSubscriptions,
    subscribeToReleaseNotifications,
    unsubscribeFromReleaseNotifications,
} from "../../utils/notificationUtil";
import {sx} from "./styles";
import theme from "../../theme";
import {AlertSwitch} from "../../components/AlertSwitch/AlertSwitch";
import {ToolsUsageMap, toolUsageApi} from "../../utils/toolUsageUtil";

export default function ManageSubscriptions(): JSX.Element {
    const {getAccessToken} = useAuth();
    const [tools, setTools] = useState<Tool[] | null>(null);
    const [usageMap, setUsageMap] = useState<ToolsUsageMap>({});
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    // modal state
    const [qtyModalOpen, setQtyModalOpen] = useState(false);
    const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
    const [quantity, setQuantity] = useState<number>(1);

    // Alert Toggle States
    const [notificationToolCodes, setNotificationToolCodes] = useState<Set<string>>(new Set());
    const [notifBusy, setNotifBusy] = useState<string | null>(null);

    const MAX_QTY = 500;

    useEffect(() => {
        (async () => {
            try {
                // @ts-ignore
                const token = await getAccessToken();

                const [{tools}, subscribedToolCodes, map] = await Promise.all([
                    getTools(token!),
                    getReleaseNotificationSubscriptions(token!),
                    toolUsageApi.getAllToolsUsage(token!)
                ]);

                setTools(tools);
                setNotificationToolCodes(new Set(subscribedToolCodes));
                setUsageMap(map)
            } catch (e: any) {
                setErr(e?.message ?? String(e));
            }
        })();
    }, [getAccessToken]);

    const handleSubscribe = async (t: Tool, qty: number) => {
        try {
            setBusy(t.code);
            const token = await getAccessToken!();
            const url = await startCheckout(t.priceId!, qty, token!);
            if (url) window.location.assign(url);
        } catch (e: any) {
            setErr(e?.message ?? String(e));
        } finally {
            setBusy(null);
        }
    };

    const handleManage = async () => {
        try {
            setBusy("portal");
            const token = await getAccessToken!();
            const url = await openPortal(token ?? undefined);
            if (url) window.location.assign(url);
        } catch (e: any) {
            setErr(e?.message ?? String(e));
        } finally {
            setBusy(null);
        }
    };

    const openQtyModal = (t: Tool) => {
        setSelectedTool(t);
        setQuantity(1);
        setQtyModalOpen(true);
    };

    const closeQtyModal = () => {
        setQtyModalOpen(false);
        setSelectedTool(null);
        setQuantity(1);
    };

    const confirmQtyAndCheckout = async () => {
        if (!selectedTool) return;

        // basic guard
        const qty = Number.isFinite(quantity) ? quantity : 1;
        const safeQty = Math.max(1, Math.min(999, Math.floor(qty)));

        setQtyModalOpen(false);
        await handleSubscribe(selectedTool, safeQty);
    };

    const handleAlertToggle = async (toolCode: string, nextValue: boolean) => {
        try {
            setNotifBusy(toolCode);
            const token = await getAccessToken!();

            if (nextValue) {
                await subscribeToReleaseNotifications(toolCode, token ?? undefined);
                // @ts-ignore
                setNotificationToolCodes((prev) => new Set([...prev, toolCode]));
            } else {
                await unsubscribeFromReleaseNotifications(toolCode, token ?? undefined);
                setNotificationToolCodes((prev) => {
                    const next = new Set(prev);
                    next.delete(toolCode);
                    return next;
                });
            }
        } catch (e: any) {
            setErr(e?.message ?? String(e));
        } finally {
            setNotifBusy(null);
        }
    };


    if (err)
        return (
            <Paper sx={sx.page}>
                <Typography color="error" align="center" fontWeight={700}>
                    {err}
                </Typography>
            </Paper>
        );

    if (!tools)
        return (
            <Paper sx={sx.page}>
                <Typography align="center">Loading…</Typography>
            </Paper>
        );

    return (
        <Box>
            <Typography
                variant="h4"
                color={theme.palette.primary.contrastText}
                fontWeight={"bold"}
                p={3}
            >
                Manage Subscriptions
            </Typography>

            <Box sx={sx.grid}>
                {tools.map((t) => (
                    <Card
                        key={t.code}
                        variant={(t.status === "active" ? "gradient" : undefined) as any}
                        sx={{textAlign: "center", p: 2}}
                    >
                        <Typography
                            variant="h5"
                        >
                            {t.name}
                        </Typography>

                        {
                            // If the user is subscribed to the tool, show how many units they purchased, and
                            // how many they have used.
                            t.subscribed &&
                            <Typography variant="h6" color="secondary">
                                {usageMap[t.code]?.usedQty || "-"} / {usageMap[t.code]?.subscribedQty || "-"} {t.unitName} used.
                            </Typography>
                        }

                        <CardContent sx={sx.body(t.subscribed)}>
                            <Box
                                component="img"
                                src={`/tool_icons/${t.code}.png`}
                                alt={`${t.name} icon`}
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).src = "/tool_icons/NRTB.png";
                                }}
                                style={sx.icon}
                            />

                            <Box sx={sx.col} textTransform={"uppercase"} fontWeight={"bold"}>
                                <Chip color="primary" label={t.status === "none" ? "NOT SUBSCRIBED" : t.status}/>

                                <Tooltip
                                    title={<Typography>{t.longBlurb || t.blurb || "No description available."}</Typography>}
                                    arrow
                                    placement="bottom"
                                >
                                    <Chip variant="outlined" color="primary" label="INFO" clickable/>
                                </Tooltip>

                                <Chip
                                    variant="outlined"
                                    color="primary"
                                    label={["active", "incomplete"].includes(t.status) ? "MANAGE" : "BUY"}
                                    disabled={busy === t.code || busy === "portal" || !t.isReleased }
                                    onClick={() => (["active", "incomplete"].includes(t.status) ? handleManage() : openQtyModal(t))}
                                    clickable
                                />

                                <Chip
                                    variant={"outlined"}
                                    color="primary"
                                    label={t.isReleased ? "OUT NOW" : `COMING SOON: ${t.releaseDate}`}
                                />
                                <AlertSwitch
                                    value={notificationToolCodes.has(t.code)}
                                    disabled={notifBusy === t.code}
                                    onChange={(next) => handleAlertToggle(t.code, next)}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* NEW: Quantity modal */}
            <Dialog open={qtyModalOpen} onClose={closeQtyModal} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Select quantity for {selectedTool?.name}
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{mb: 2}}>
                        Choose how many {selectedTool?.unitName || "units"} you want to purchase.
                    </Typography>

                    <TextField
                        label="Quantity"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(parseInt(e.target.value || "1", 10))}
                        inputProps={{min: 1, max: 999, step: 1}}
                        fullWidth
                    />

                    {
                        (quantity > MAX_QTY) &&
                        <Alert severity="info" sx={{mt:2}}>
                            To purchase more than {MAX_QTY} {selectedTool?.unitName || "units"}, contact us at &nbsp;
                            <Link href="mailto:newrelic@intelliplatforms.com">
                                newrelic@intelliplatforms.com
                            </Link>
                        </Alert>
                    }

                </DialogContent>

                <DialogActions>
                    <Button onClick={closeQtyModal} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmQtyAndCheckout}
                        variant="contained"
                        disabled={ (!selectedTool) || (quantity > MAX_QTY) || (quantity < 1) || (!Number.isFinite(quantity)) }
                    >
                        Continue to Checkout
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
